import { store } from "src/redux/store/Store";
import WebsocketClient from "src/websockets/WebsocketClient";
import { getEventsByFlight, removeFlightEvents } from "src/redux/reducers/OpsLinkReducer";
import { IFlight } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { IAircraftFlights } from "src/redux/reducers/FlightTimelineReducer";

/** Tracks flight IDs we previously loaded for dispatch desk sync so we can
 *  clean up events when flights are removed from the desk. */
let previousDispatchDeskFlightIds: Set<string> = new Set();

// ---------------------------------------------------------------------------
// Dispatch Desk → OpsLink flight subscription sync
//
// Dispatchers are assigned to desks. Each desk has flights. We want the
// dispatcher to automatically receive OpsLink real-time updates for every
// flight on desks they belong to.
//
// This module reads the current Redux state, determines which flights the
// logged-in user should be subscribed to (based on dispatch desk membership),
// and sends WebSocket subscribe messages for each of those flights.
//
// Additionally, we pre-fetch OpsLink events for those flights so the
// unacknowledged-message counter in the status bar can display accurate
// counts from the moment the app starts.
//
// Re-subscribing is idempotent — the backend ignores duplicate subscriptions.
// When the WebSocket connection recycles (~30 min), old subscriptions are
// dropped and this function is called again to re-establish them.
// ---------------------------------------------------------------------------

/**
 * Returns all unique flight IDs from dispatch desks that the current user is
 * a member of.
 */
export function getDispatchDeskFlightIdsForCurrentUser(): string[] {
  const state = store.getState();
  const currentUserId = state.authentication?.attributes?._id;

  if (!currentUserId) {
    console.warn("[DispatchDesk↔OpsLink] No current user ID in authentication state — skipping.");
    return [];
  }

  const desks = state.dispatchDesks?.desks ?? [];

  if (desks.length === 0) {
    console.warn("[DispatchDesk↔OpsLink] No dispatch desks in Redux state.");
    return [];
  }

  // Find desks where the current user is assigned
  const userDesks = desks.filter((desk) => desk.users.some((u) => u._id === currentUserId));

  if (userDesks.length === 0) {
    // Log all desk user IDs for debugging
    const allDeskUserIds = desks.flatMap((d) => d.users.map((u) => u._id));
    console.warn(`[DispatchDesk↔OpsLink] User ${currentUserId} is not on any desk. Desk user IDs:`, allDeskUserIds);
    return [];
  }

  // Collect all unique flight IDs across those desks
  const flightIds = new Set<string>();
  userDesks.forEach((desk) => {
    desk.flights.forEach((flight) => {
      flightIds.add(flight._id);
    });
  });
  return Array.from(flightIds);
}

/**
 * Subscribe to OpsLink updates for all flights on the current user's dispatch
 * desks via the main WebSocket connection.
 *
 * Safe to call at any time — if the WebSocket isn't connected or the user has
 * no dispatch desk flights, this is a no-op.
 */
export function subscribeToDispatchDeskFlights(): void {
  const flightIds = getDispatchDeskFlightIdsForCurrentUser();

  if (flightIds.length === 0) return;

  flightIds.forEach((flightId) => {
    WebsocketClient.send({ action: "subscribe", flightId });
  });
}

/**
 * Fetch OpsLink events for every flight on the current user's dispatch desks.
 *
 * This ensures the OpsLink unacknowledged-message counter in the status bar
 * has data to count against — even before the user opens an OpsLink window.
 */
export function fetchEventsForDispatchDeskFlights(): void {
  const flightIds = getDispatchDeskFlightIdsForCurrentUser();
  const currentSet = new Set(flightIds);

  // Remove events for flights that were previously tracked but are no longer
  // on the user's dispatch desk(s).
  const staleFlightIds = [...previousDispatchDeskFlightIds].filter((id) => !currentSet.has(id));
  if (staleFlightIds.length > 0) {
    store.dispatch(removeFlightEvents(staleFlightIds));
  }

  // Update the tracking set
  previousDispatchDeskFlightIds = currentSet;

  if (flightIds.length === 0) return;

  flightIds.forEach((flightId) => {
    store.dispatch(getEventsByFlight({ flightId }));
  });
}

// ---------------------------------------------------------------------------
// Store watcher – automatically syncs whenever dispatchDesks.desks changes
// ---------------------------------------------------------------------------

let watcherUnsubscribe: (() => void) | null = null;

/**
 * Start watching Redux store for dispatch desk changes.
 *
 * Whenever `state.dispatchDesks.desks` changes (from any source — thunk
 * fulfillments, WebSocket handler re-fetches, etc.) we automatically
 * re-subscribe to OpsLink for the updated flight list and fetch/clean up
 * events so the status bar counter stays accurate.
 *
 * Call once after login (e.g. in App.tsx).
 */
export function initDispatchDeskOpsLinkWatcher(): void {
  // Avoid duplicate watchers if called more than once.
  if (watcherUnsubscribe) return;

  let prevDesksRef: any = store.getState().dispatchDesks?.desks;

  watcherUnsubscribe = store.subscribe(() => {
    const currentDesks = store.getState().dispatchDesks?.desks;
    // Immer gives us a new reference only when the value actually changed.
    if (currentDesks !== prevDesksRef) {
      prevDesksRef = currentDesks;
      subscribeToDispatchDeskFlights();
      fetchEventsForDispatchDeskFlights();
    }
  });
}

// ---------------------------------------------------------------------------
// Resolve full IFlight objects — timeline-first, API fallback
// ---------------------------------------------------------------------------

/**
 * Given a list of flight IDs, return full IFlight objects.
 *
 * 1. First looks in `state.timelineFlights.flightData` (flights already loaded
 *    on the Gantt). This is free — no API calls.
 * 2. For any IDs not found in Redux, fetches them individually from the API.
 *
 * Returns only the flights that were successfully resolved (skips nulls).
 */
export async function resolveFullFlights(flightIds: string[]): Promise<IFlight[]> {
  if (flightIds.length === 0) return [];

  const resolved: IFlight[] = [];
  const missing: string[] = [];

  // Search the timeline flight data in Redux
  const flightData: IAircraftFlights | null = store.getState().timelineFlights?.flightData;
  const lookupMap = new Map<string, IFlight>();

  if (flightData) {
    for (const acKey of Object.keys(flightData)) {
      const events = flightData[acKey];
      if (!events?.flights) continue;
      for (const f of events.flights) {
        if (f?._id) lookupMap.set(f._id, f);
      }
    }
  }

  for (const id of flightIds) {
    const cached = lookupMap.get(id);
    if (cached) {
      resolved.push(cached);
    } else {
      missing.push(id);
    }
  }

  // Fetch only the ones not already in Redux — single bulk call
  if (missing.length > 0) {
    try {
      const fetched = await AmplifyAPIWrapper.Post<IFlight[]>("/flights/getFlights", missing);
      if (fetched) {
        resolved.push(...fetched);
      }
    } catch (e: any) {
      console.error("[DispatchDesk↔OpsLink] Error fetching flights:", e?.response ?? e);
    }
  } else {
  }

  return resolved;
}
